import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "../../models/MechanicalTestRunDisposition";

export type RunGroup = {
	runId: number;
	reception: Date | null;
	reported: Date | null;
	results: HeatMechanicalResult[];
	history: MechanicalTestRunDisposition[];
	disposition: MechanicalTestRunDisposition | null;
	comment: string | null;
};

const byDisposedAtDesc = (
	a: MechanicalTestRunDisposition,
	b: MechanicalTestRunDisposition,
) => new Date(b.DisposedAt).getTime() - new Date(a.DisposedAt).getTime();

const byReceptionDesc = (a: RunGroup, b: RunGroup) => {
	if (!a.reception) return 1;
	if (!b.reception) return -1;
	return new Date(b.reception).getTime() - new Date(a.reception).getTime();
};

// Id of the placeholder group. The heat has no run at all, so there is no real id to use and the
// save path swaps it for the heat -- see PLACEHOLDER_RUN_ID in useSaveMechanicalRun.
export const PLACEHOLDER_RUN_ID = 0;

// allowEmptyRun asks for a placeholder group when the heat has nothing: the lab file never arrived
// and the lab manager still has to be able to rule on the heat. Off by default, so the screens that
// dispose from their own grid keep showing nothing when there is nothing.
export const buildRunGroups = (
	results: HeatMechanicalResult[],
	dispositions: MechanicalTestRunDisposition[],
	allowEmptyRun = false,
): RunGroup[] => {
	const byRun = new Map<number, HeatMechanicalResult[]>();

	for (const result of results) {
		const group = byRun.get(result.MechanicalTestRunId);
		if (group) group.push(result);
		else byRun.set(result.MechanicalTestRunId, [result]);
	}

	for (const disposition of dispositions) {
		if (!byRun.has(disposition.MechanicalTestRunId))
			byRun.set(disposition.MechanicalTestRunId, []);
	}

	if (byRun.size === 0 && allowEmptyRun)
		return [
			{
				runId: PLACEHOLDER_RUN_ID,
				reception: null,
				reported: null,
				results: [],
				history: [],
				disposition: null,
				comment: null,
			},
		];

	return Array.from(byRun.entries())
		.map(([runId, rows]) => {
			const history = dispositions
				.filter((d) => d.MechanicalTestRunId === runId && d.IsActive !== false)
				.sort(byDisposedAtDesc);
			const disposition = history[0] ?? null;

			return {
				runId,
				reception: rows[0]?.Reception ?? disposition?.Reception ?? null,
				reported: rows[0]?.Reported ?? disposition?.Reported ?? null,
				results: rows,
				history,
				disposition,
				comment: disposition?.Comment ?? null,
			};
		})
		.sort(byReceptionDesc);
};
